import { Check, Pause, PenLine } from "lucide-react";

import { Section, SectionHeading } from "@/components/ui/Section";
import { AuroraField } from "@/components/visuals/AuroraField";
import { useLocale } from "@/i18n";
import { cn } from "@/lib/cn";

/**
 * Boardroom preview.
 *
 * Agents are laid out around an ellipse to read as a 2.5D meeting table on
 * wider screens, and collapse to a plain vertical list on narrow ones — a ring
 * of absolutely positioned nodes is unusable on a phone.
 *
 * The status lines are human-readable progress, deliberately not a transcript
 * of internal reasoning.
 *
 * This is an illustration of the surface. It is not connected to a backend and
 * shows no real session.
 */

/** Five evenly spaced points on an ellipse, starting at the top. */
const SEATS = [
  { x: 50, y: 18 },
  { x: 88, y: 40.7 },
  { x: 73.5, y: 76 },
  { x: 26.5, y: 76 },
  { x: 12, y: 40.7 },
] as const;

export function BoardroomPreview() {
  const { t } = useLocale();
  const a = t.boardroom.agents;

  const agents: Array<{
    name: string;
    status: string;
    done: boolean;
    isDirector: boolean;
  }> = [
    { ...a.architect, done: true, isDirector: false },
    { ...a.strategy, done: true, isDirector: false },
    { ...a.marketing, done: false, isDirector: false },
    { ...a.finance, done: false, isDirector: false },
    { ...a.director, done: false, isDirector: true },
  ];

  return (
    <div className="relative overflow-hidden">
      <AuroraField variant="soft" />

      <Section id="boardroom" className="relative">
        <SectionHeading title={t.boardroom.title} subtitle={t.boardroom.subtitle} />

        <div className="card-surface mx-auto mt-16 max-w-5xl overflow-hidden">
          {/* Session header */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/8 px-6 py-4">
            <span className="inline-flex items-center gap-2 text-sm text-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-soft" />
              {t.boardroom.liveLabel}
            </span>
            <span className="text-sm text-cream-100/45">
              {t.boardroom.roundLabel}{" "}
              <span className="tabular text-cream-50">1</span>{" "}
              {t.boardroom.ofRounds}
            </span>
          </div>

          {/* --- Wide screens: the round table --- */}
          <div className="relative hidden h-[26rem] md:block">
            {/* Table surface */}
            <div
              aria-hidden
              className={cn(
                "absolute left-1/2 top-1/2 h-[13rem] w-[27rem] -translate-x-1/2 -translate-y-1/2",
                "rounded-[50%] border border-white/8",
                "bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.10),transparent_70%)]",
              )}
            />
            <div
              aria-hidden
              className="absolute left-1/2 top-1/2 h-[7rem] w-[16rem] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-emerald-500/[0.06] blur-2xl pulse-soft"
            />

            {agents.map((agent, i) => {
              const seat = SEATS[i]!;
              return (
                <div
                  key={agent.name}
                  className="rise-in absolute w-40 -translate-x-1/2 -translate-y-1/2 text-center"
                  /* Physical `left`, deliberately: the seat ring is
                     left/right symmetric, so RTL needs no mirroring — and
                     `insetInlineStart` paired with the physical
                     -translate-x-1/2 double-shifts under RTL, clipping the
                     side seats out of the card (seen on /ar). */
                  style={{
                    left: `${seat.x}%`,
                    top: `${seat.y}%`,
                    animationDelay: `${i * 130}ms`,
                  }}
                >
                  <Seat {...agent} />
                </div>
              );
            })}
          </div>

          {/* --- Narrow screens: a readable list --- */}
          <ul className="divide-y divide-white/6 md:hidden">
            {agents.map((agent) => (
              <li key={agent.name} className="flex items-start gap-3 px-6 py-4">
                <Dot done={agent.done} isDirector={agent.isDirector} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-cream-50">{agent.name}</p>
                  <p className="mt-0.5 text-[0.8rem] text-cream-100/50">
                    {agent.status}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Round summary */}
          <div className="border-t border-white/8 px-6 py-5">
            <p className="text-[0.72rem] tracking-wide text-cream-100/40">
              {t.boardroom.summaryLabel}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-cream-100/70">
              {t.boardroom.summary}
            </p>
          </div>

          {/* Approval gate */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/8 bg-white/[0.02] px-6 py-4">
            <p className="text-sm text-lavender-200/85">
              {t.boardroom.approvalPrompt}
            </p>
            <div className="flex flex-wrap gap-2">
              <GateButton icon={Check} label={t.boardroom.actions.approve} primary />
              <GateButton icon={PenLine} label={t.boardroom.actions.revise} />
              <GateButton icon={Pause} label={t.boardroom.actions.stop} />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Dot({ done, isDirector }: { done: boolean; isDirector?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "mt-1 flex h-2.5 w-2.5 shrink-0 rounded-full",
        done
          ? "bg-emerald-400"
          : isDirector
            ? "bg-lavender-300 pulse-soft"
            : "bg-cream-100/25",
      )}
    />
  );
}

function Seat({
  name,
  status,
  done,
  isDirector,
}: {
  name: string;
  status: string;
  done: boolean;
  isDirector?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span
        aria-hidden
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-full border text-sm font-semibold",
          done
            ? "border-emerald-400/35 bg-emerald-500/12 text-emerald-200"
            : isDirector
              ? "border-lavender-400/40 bg-lavender-500/12 text-lavender-200"
              : "border-white/12 bg-white/[0.04] text-cream-100/55",
        )}
      >
        {done ? <Check size={16} strokeWidth={2.5} /> : <Dot done={false} isDirector={isDirector} />}
      </span>
      <p className="text-[0.82rem] font-medium text-cream-50">{name}</p>
      <p className="text-[0.72rem] leading-snug text-cream-100/45">{status}</p>
    </div>
  );
}

function GateButton({
  icon: Icon,
  label,
  primary,
}: {
  icon: typeof Check;
  label: string;
  primary?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[0.8rem]",
        primary
          ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30"
          : "text-cream-100/50 ring-1 ring-white/10",
      )}
    >
      <Icon size={13} aria-hidden />
      {label}
    </span>
  );
}
